import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatBRL } from "../../lib/currency";
import { ReportExport } from "../../components/ReportExport";

interface ReportsData {
  kpis: {
    totalRevenueCents: number;
    paidOrdersCount: number;
    pendingOrdersCount: number;
    refundedOrdersCount: number;
    totalOrdersCount: number;
    conversionRate: number;
    activeTenantsCount: number;
    totalLicenseCapacity: number;
    consumedLicensesCount: number;
    availableCatalogCount: number;
  };
  topSellingItems: {
    id: string;
    title: string;
    sku: string;
    salesCount: number;
  }[];
}

export function AdminReportsPage() {
  const [data, setData] = useState<ReportsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<ReportsData>("/admin/reports")
      .then((res) => setData(res))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar relatórios."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div>
        <h2>Relatórios e Métricas</h2>
        <p className="text-muted">Calculando indicadores da plataforma...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <h2>Relatórios e Métricas</h2>
        <p role="alert" className="notice-error">{error ?? "Não foi possível carregar os dados."}</p>
      </div>
    );
  }

  const { kpis, topSellingItems } = data;

  return (
    <ReportExport title="Relatórios e métricas">

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "var(--space-4)",
          marginBottom: "var(--space-6)",
        }}
      >
        <div className="card">
          <span className="text-muted" style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-label)" }}>
            Faturamento Confirmado (Pix)
          </span>
          <p style={{ fontSize: "var(--text-3xl)", fontWeight: 700, margin: "var(--space-2) 0 0", color: "var(--color-accent)" }}>
            {formatBRL(kpis.totalRevenueCents)}
          </p>
          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
            {kpis.paidOrdersCount} pedidos pagos
          </span>
        </div>

        <div className="card">
          <span className="text-muted" style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-label)" }}>
            Total de Pedidos
          </span>
          <p style={{ fontSize: "var(--text-3xl)", fontWeight: 700, margin: "var(--space-2) 0 0" }}>
            {kpis.totalOrdersCount}
          </p>
          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
            {kpis.pendingOrdersCount} pendentes / {kpis.refundedOrdersCount} reembolsos
          </span>
        </div>

        <div className="card">
          <span className="text-muted" style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-label)" }}>
            Taxa de Conversão Pix
          </span>
          <p style={{ fontSize: "var(--text-3xl)", fontWeight: 700, margin: "var(--space-2) 0 0" }}>
            {kpis.conversionRate.toFixed(1)}%
          </p>
          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
            Proporção de checkouts pagos
          </span>
        </div>

        <div className="card">
          <span className="text-muted" style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-label)" }}>
            Licenciamento B2B
          </span>
          <p style={{ fontSize: "var(--text-3xl)", fontWeight: 700, margin: "var(--space-2) 0 0" }}>
            {kpis.consumedLicensesCount} / {kpis.totalLicenseCapacity}
          </p>
          <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
            {kpis.activeTenantsCount} tenants ativos
          </span>
        </div>
      </div>

      <section className="card" style={{ marginBottom: "var(--space-6)" }}>
        <h3>Top Obras Mais Vendidas</h3>
        {topSellingItems.length === 0 ? (
          <p className="text-muted">Ainda não há vendas registradas para ranqueamento.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Título da Obra</th>
                <th className="num">Vendas Confirmadas</th>
              </tr>
            </thead>
            <tbody>
              {topSellingItems.map((item) => (
                <tr key={item.id}>
                  <td><code>{item.sku}</code></td>
                  <td><strong>{item.title}</strong></td>
                  <td className="num">{item.salesCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h3>Resumo do Catálogo e Plataforma</h3>
        <ul style={{ paddingLeft: "var(--space-4)", lineHeight: "var(--leading-normal)" }}>
          <li><strong>Obras disponíveis para venda:</strong> {kpis.availableCatalogCount} títulos</li>
          <li><strong>Empresas corporativas (Tenants):</strong> {kpis.activeTenantsCount} empresas com contrato ativo</li>
          <li><strong>Capacidade total de licenças alocadas:</strong> {kpis.totalLicenseCapacity} licenças</li>
        </ul>
      </section>
    </ReportExport>
  );
}
